// @ts-ignore
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Info, Star, X, MessageSquarePlus } from "lucide-react";
import { Specialist } from "../types";
import { WidgetHeader } from "../../../components/WidgetHeader";
import ReviewSubmitForm from "./ReviewSubmitForm";
import { useTranslation } from "react-i18next";

// Modal component for displaying specialist information
function SpecialistInfoModal({
  specialist,
  onClose,
}: {
  specialist: Specialist | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [showReviewForm, setShowReviewForm] = React.useState(false);
  const reviewLocale = i18n.resolvedLanguage || i18n.language || "en";
  const reviewDateFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(reviewLocale, {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [reviewLocale],
  );

  if (!specialist) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal content */}
      <div
        className="relative z-10 w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-6 pb-4 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center overflow-hidden ring-2 ring-primary/20 shrink-0">
                {specialist.avatar ? (
                  <img
                    src={specialist.avatar}
                    alt={specialist.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Users className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{specialist.name}</h3>
                {specialist.title && (
                  <p className="text-sm text-muted-foreground">{specialist.title}</p>
                )}
              </div>
            </div>
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted/50 transition-colors"
              aria-label={t("widget.common.close", "Close")}
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 pt-2">
          {/* Information Section */}
          {specialist.information ? (
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Info className="w-4 h-4" />
                <span>{t("widget.specialists.about", "About the specialist")}</span>
              </div>
              <p className="text-foreground leading-relaxed whitespace-pre-line text-sm">
                {specialist.information}
              </p>
            </div>
          ) : (
            <div className="mb-6">
              <p className="text-muted-foreground text-center py-4 text-sm">
                {t("widget.specialists.no_info", "Information about this specialist has not been added yet.")}
              </p>
            </div>
          )}

          {/* Leave Review Button / Form */}
          <div className="mb-4">
            {showReviewForm ? (
              <ReviewSubmitForm
                staffId={Number(specialist.id)}
                onCancel={() => setShowReviewForm(false)}
                onSuccess={() => setShowReviewForm(false)}
              />
            ) : (
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl gap-2 border-primary/30 hover:bg-primary/5"
                onClick={() => setShowReviewForm(true)}
              >
                <MessageSquarePlus className="w-5 h-5 text-primary" />
                <span className="font-medium">{t("widget.reviews.leave", "Leave a review")}</span>
              </Button>
            )}
          </div>

          {/* Reviews Section */}
          {specialist.reviews && specialist.reviews.length > 0 && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Star className="w-4 h-4" />
                  <span>{t("widget.reviews.title", "Reviews")} ({specialist.reviewCount})</span>
                </div>
                {specialist.rating > 0 && (
                  <div className="flex items-center gap-1 bg-yellow-400/10 px-2 py-0.5 rounded-md">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs font-bold text-yellow-700 dark:text-yellow-400">{specialist.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {specialist.reviews.map((review, i) => (
                  <div key={i} className="bg-muted/30 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{review.reviewer_name}</span>
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, starI) => (
                          <Star
                            key={starI}
                            className={`w-3 h-3 ${starI < review.rating ? "fill-current" : "text-muted/30"}`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-foreground/90 leading-snug">
                        {review.comment}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {reviewDateFormatter.format(new Date(review.date))}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SpecialistsView({
  specialists,
  selectedId,
  onBack,
  onSelect,
  onContinue,
  ctaLabel,
  onGoToSpecialist,
  onGoToServices,
  onOpenClientDashboard,
  onOpenSupport,
}: {
  specialists: Specialist[];
  selectedId: string | null;
  onBack: () => void;
  onSelect: (id: string) => void;
  onContinue?: () => void;
  ctaLabel?: string;
  onGoToSpecialist: () => void;
  onGoToServices: () => void;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
}) {
  const { t } = useTranslation();
  const [infoModalSpecialist, setInfoModalSpecialist] = React.useState<Specialist | null>(null);

  React.useEffect(() => {
    if (selectedId) {
      const el = document.getElementById(`specialist-card-${selectedId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [selectedId]);

  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          <WidgetHeader
            onBack={onBack}
            onGoToSpecialist={onGoToSpecialist}
            onGoToServices={onGoToServices}
            onOpenClientDashboard={onOpenClientDashboard}
            onOpenSupport={onOpenSupport}
          />

          {/* Scrollable content; bottom padding equals sticky bar height + safe area */}
          <div
            className="flex-1 overflow-y-auto p-6"
            style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
          >
            {/* Title */}
            <div className="pb-2">
              <h2 className="text-2xl font-bold text-foreground">{t("widget.specialists.choose", "Choose specialist")}</h2>
            </div>

            {/* Specialists List */}
            <div className="space-y-4">
              {specialists.map((specialist) => (
                <div
                  key={specialist.id}
                  id={`specialist-card-${specialist.id}`}
                  className="space-y-3 scroll-mt-24"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar or Icon */}
                    <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center overflow-hidden">
                      {specialist.id === "any" ? (
                        <Users className="w-6 h-6 text-muted-foreground" />
                      ) : (
                        <img
                          src={specialist.avatar || "/placeholder.svg"}
                          alt={specialist.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Specialist Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base">{specialist.name}</h3>
                        {specialist.id !== "any" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-auto"
                            onClick={(e) => {
                              e.stopPropagation();
                              setInfoModalSpecialist(specialist);
                            }}
                          >
                            <Info className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>

                      {specialist.title && <p className="text-sm text-muted-foreground">{specialist.title}</p>}

                      {specialist.rating > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setInfoModalSpecialist(specialist);
                            }}
                          >
                            {t("widget.reviews.count", "{{count}} reviews", { count: specialist.reviewCount })}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Radio Button */}
                    <div
                      className="w-6 h-6 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center cursor-pointer"
                      onClick={() => onSelect(specialist.id)}
                    >
                      {selectedId === specialist.id && <div className="w-3 h-3 rounded-full bg-primary" />}
                    </div>
                  </div>

                  {/* Available Time Slots */}
                  {specialist.availableSlots.map((slot, index) => (
                    <div key={index} className="ml-16 space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {t("widget.specialists.next_slot", "Next available time")} <span className="font-medium">{slot.day}:</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {slot.times.map((time) => (
                          <Button
                            key={time}
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 text-sm rounded-full bg-muted/30 border-muted hover:bg-muted/50"
                            onClick={() => onSelect(specialist.id)}
                          >
                            {time}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Sticky CTA/status bar (participates in layout; no overlap) */}
          {selectedId != null && onContinue && (
            <div
              className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
            >
              <div className="p-4 pt-3 space-y-3">
                <button
                  className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold shadow-lg"
                  onClick={onContinue}
                  aria-label={ctaLabel ?? t("widget.services.choose", "Choose service")}
                >
                  {ctaLabel ?? t("widget.services.choose", "Choose service")}
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Info Modal */}
      {infoModalSpecialist && (
        <SpecialistInfoModal
          specialist={infoModalSpecialist}
          onClose={() => setInfoModalSpecialist(null)}
        />
      )}
    </div>
  );
}
